import { createContext, FC, useCallback, useEffect, useMemo, useState } from "react";

// store
import { detectLanguage } from "store/slices/language-slice";
import { useAppDispatch } from "store/store";

// hooks
import useLanguage from "hooks/use-language";

import api from "services/api";

// common
import serveRequestErrors from "common/serve-request-errors";

import ErrorResponseDto from "dto/services/error-response-dto";
import { ResourceContentDto } from "dto/services/resource-data-dto";
import TemplateContextDto, { Props } from "dto/contexts/template-context-dto";

import AppScreenLoader from "components/app-screen-loader";

const TemplateContext = createContext<TemplateContextDto>({} as TemplateContextDto);

const TemplateProvider: FC<Props> = ({ children }) => {
	const dispatch = useAppDispatch();
	const { syncLanguageFromUrl } = useLanguage({ autoDetect: true });
	const [activeTemplateData, setActiveTemplateData] = useState<string>("");
	const [resourceData, setResourceData] = useState<ResourceContentDto[]>([]);

	const onHandleGetResourceData = useCallback(async () => {
		try {
			const templateResponse = await api.get.resource.template();
			const templateData = templateResponse.data.data;

			if (!templateData) {
				return;
			}

			if (templateData !== "template_1") {
				document.documentElement?.setAttribute("data-theme", "theme2");
			}

			const resolvedLanguage = syncLanguageFromUrl();

			const response = await api.get.resource.resource(templateData, resolvedLanguage);
			setResourceData(response.data.data.resourceConfig.contents);
			setActiveTemplateData(templateData);
		} catch (error: unknown) {
			const err = error as Error | ErrorResponseDto;
			serveRequestErrors(err);
		}
	}, [syncLanguageFromUrl]);

	const getFilteredResources = useMemo(() => {
		return (page: string) => {
			return resourceData.find((content) => content.page === page)?.values;
		};
	}, [resourceData]);

	const contextValue = useMemo(() => ({ getFilteredResources, activeTemplateData }), [getFilteredResources, activeTemplateData]);

	useEffect(() => {
		onHandleGetResourceData();
	}, [onHandleGetResourceData]);

	useEffect(() => {
		// Detect language on app mount
		dispatch(detectLanguage());
	}, [dispatch]);

	return <TemplateContext.Provider value={contextValue}>{resourceData.length > 0 ? children : <AppScreenLoader />}</TemplateContext.Provider>;
};

export { TemplateProvider, TemplateContext };
